import buildAssetLabels from './labelFilter'
import { storyAssociations } from './storyFilter'
import tags from './tagFilter'

const storySection = (sidebarSettings) => {
  if (sidebarSettings.availableStories.length === 0) {
    return null
  }

  return {
    id: 'story-associations',
    title: 'Story Associations',
    icon: 'story',
    childOptions: storyAssociations(sidebarSettings),
  }
}

const tagSection = (sidebarSettings) => {
  if (sidebarSettings.availableTags.length === 0) {
    return null
  }

  return {
    id: 'tags',
    title: 'Tags',
    icon: 'label',
    childOptions: tags(sidebarSettings),
  }
}

const labelSection = (sidebarSettings) => {
  if (sidebarSettings.availableLabels.length === 0) {
    return null
  }

  return {
    id: 'labels',
    title: 'Labels',
    icon: 'label',
    childOptions: buildAssetLabels(sidebarSettings),
  }
}

// build the asset filter tree with data coming from the asset search sidebar
// all childOptions must have both an id and payload.object.id.
// for childOptions that represent back-end objects (like stories), the id
// must be a globally unique identifier. when that object is selected, it's
// id is added to an array of selected items. because many of our models use
// simple counting ids, they are not globally unique. so we use the
// childOption.id (UUID) to track selected items in react, but then send the
// payload.object.id to the back-end
const libraryFilterTree = (sidebarSettings) => {
  const filterTree = [
    storySection(sidebarSettings),
    {
      id: 'media-type',
      title: 'Media Type',
      icon: 'channel-photo',
      childOptions: [
        {
          id: 'image',
          title: 'Image',
          icon: 'channel-photo',
          payload: {
            type_key: 'assetMediaType',
            object: {
              id: 'image',
            },
          },
        },
        {
          id: 'video',
          title: 'Video',
          icon: 'channel-video-14',
          childOptions: [],
          payload: {
            type_key: 'assetMediaType',
            object: {
              id: 'video',
            },
          },
        },
        {
          id: 'audio',
          title: 'Audio',
          icon: 'channel-audio-sm',
          childOptions: [],
          payload: {
            type_key: 'assetMediaType',
            object: {
              id: 'audio',
            },
          },
        },
        {
          id: 'file',
          title: 'File',
          icon: 'file',
          childOptions: [],
          payload: {
            type_key: 'assetMediaType',
            object: {
              id: 'file',
            },
          },
        },
      ],
    },
    {
      id: 'usage',
      title: 'Usage Rights',
      icon: 'gem-calendar',
      childOptions: [
        {
          id: 'license',
          title: 'License',
          icon: 'gem-calendar',
          childOptions: [
            {
              id: 'asset_license_creative_commons',
              title: 'Creative Commons',
              icon: 'gem-calendar',
              childOptions: [],
              payload: {
                type_key: 'assetUsageRights',
                object: {
                  id: 'asset_license_creative_commons',
                },
              },
            },
            {
              id: 'asset_license_rights_managed',
              title: 'Rights Managed',
              icon: 'gem-calendar',
              childOptions: [],
              payload: {
                type_key: 'assetUsageRights',
                object: {
                  id: 'asset_license_rights_managed',
                },
              },
            },
            {
              id: 'asset_license_royalty_free',
              title: 'Royalty Free',
              icon: 'gem-calendar',
              childOptions: [],
              payload: {
                type_key: 'assetUsageRights',
                object: {
                  id: 'asset_license_royalty_free',
                },
              },
            },
          ],
        },
        {
          id: 'asset_approved',
          title: 'Available Assets',
          icon: '',
          context_type: 'story',
          context_id: sidebarSettings.contextId,
          childOptions: [],
        },
        {
          id: 'asset_unapproved',
          title: 'Unavailable Assets',
          icon: '',
          childOptions: [],
        },
      ],
    },
    {
      id: 'aspect',
      title: 'Aspect Ratio',
      icon: 'channel-photo',
      childOptions: [
        {
          id: 'landscape',
          title: 'Landscape',
          icon: 'channel-photo',
          childOptions: [],
          payload: {
            type_key: 'assetAspectRatio',
            object: {
              id: 'landscape',
            },
          },
        },
        {
          id: 'portrait',
          title: 'Portrait',
          icon: 'channel-photo',
          childOptions: [],
          payload: {
            type_key: 'assetAspectRatio',
            object: {
              id: 'portrait',
            },
          },
        },
        {
          id: 'square',
          title: 'Square',
          icon: 'channel-photo',
          childOptions: [],
          payload: {
            type_key: 'assetAspectRatio',
            object: {
              id: 'square',
            },
          },
        },
      ],
    },
    {
      id: 'size',
      title: 'Size',
      icon: 'channel-photo',
      color: '',
      childOptions: [
        {
          id: 'image-small',
          title: 'Small (< 1 MP)',
          icon: 'channel-photo',
          childOptions: [],
          payload: {
            type_key: 'assetChannelPhoto',
            object: {
              id: 'Small (< 1 MP)',
            },
          },
        },
        {
          id: 'image-medium',
          title: 'Medium (1-2 MP)',
          icon: 'channel-photo',
          childOptions: [],
          payload: {
            type_key: 'assetChannelPhoto',
            object: {
              id: 'Medium (1-2 MP)',
            },
          },
        },
        {
          id: 'image-large',
          title: 'Large (> 2 MP)',
          icon: 'channel-photo',
          childOptions: [],
          payload: {
            type_key: 'assetChannelPhoto',
            object: {
              id: 'Large (> 2 MP)',
            },
          },
        },
      ],
    },
    tagSection(sidebarSettings),
    labelSection(sidebarSettings),
  ]

  return filterTree.filter((section) => section != null)
}

// takes props from the assets search sidebar and returns an array of selected ids
// from the filterTree (see libraryFilterTree) that is used to display which items
// had been selected in that sidebar
export const selectedFilterValues = (sidebarFilterSettings, filterTree) => {
  const value = []

  if (filterTree) {
    const mappedTags = filterTree.find((filter) => filter.id === 'tags')
    const mappedLabels = filterTree.find((filter) => filter.id === 'labels')
    const mappedStories = filterTree.find(
      (filter) => filter.id === 'story-associations'
    )
    const selectedFilters = sidebarFilterSettings.selectedFilters

    if (mappedStories && selectedFilters.story_ids) {
      selectedFilters.story_ids.forEach((filter) => {
        const match = mappedStories.childOptions.find(
          (story) => story.payload.object.id === filter
        )
        if (match) {
          value.push(match.id)
        }
      })
    }

    if (mappedLabels && selectedFilters.labels) {
      selectedFilters.labels.forEach((filter) => {
        const match = mappedLabels.childOptions.find(
          (label) => label.payload.object.id === filter
        )
        if (match) {
          value.push(match.id)
        }
      })
    }

    if (mappedTags && selectedFilters.tags) {
      selectedFilters.tags.forEach((filter) => {
        const match = mappedTags.childOptions.find(
          (tag) => tag.payload.object.id === filter
        )
        if (match) {
          value.push(match.id)
        }
      })
    }

    if (selectedFilters.aspect_ratio) {
      value.push(...selectedFilters.aspect_ratio)
    }

    if (selectedFilters.asset_type) {
      value.push(...selectedFilters.asset_type)
    }

    if (selectedFilters.pixel_range) {
      selectedFilters.pixel_range.forEach((filter) => {
        if (filter[0] === 1 && filter[1] === 999999) {
          value.push('image-small')
        } else if (filter[0] === 1000000 && filter[1] === 2000000) {
          value.push('image-medium')
        } else if (filter[0] === 2000001 && filter[1] === '') {
          value.push('image-large')
        }
      })
    }

    if (selectedFilters.asset_license) {
      value.push(selectedFilters.asset_license)
    }

    if (selectedFilters.availability) {
      value.push('asset_approved')
    }

    if (!selectedFilters.asset_usage_status) {
      value.push('asset_unapproved')
    }
  }

  return value
}

const stringifyIds = (arrayOfInts) => {
  const arrayOfStrings = []
  if (arrayOfInts) {
    arrayOfInts.forEach((id) => {
      arrayOfStrings.push(String(id))
    })
  }
  return arrayOfStrings
}

// formats selected filters to send back to the back end
export const exportableSelectedFilters = (rawSelectedFilters) => {
  let storyIds
  if (
    rawSelectedFilters.assetStoryIds &&
    rawSelectedFilters.assetStoryIds.length > 0
  ) {
    storyIds = rawSelectedFilters.assetStoryIds
  } else {
    storyIds = rawSelectedFilters.sidebarFilterSettings.availableStories.map(
      (story) => story.id
    )
  }

  const formattedFilters = {
    story_ids: stringifyIds(storyIds),
    labels: stringifyIds(rawSelectedFilters.assetLabelIds),
    tags: stringifyIds(rawSelectedFilters.assetTagIds),
    asset_usage_status: 'asset_approved',
    filename: rawSelectedFilters.filename,
  }
  const aspectRatio = ['no-aspect-ratio-set']
  const assetType = ['no-asset-type-set']
  const availability = {}
  const pixelRange = {}
  let pixelRangeCounter = 0
  const usageSection = rawSelectedFilters.filterTree.find(
    (section) => section.id === 'usage'
  )
  const approvedSection = usageSection.childOptions.find(
    (child) => child.id === 'asset_approved'
  )
  const filterProps = rawSelectedFilters.filterProps
  let removeAssetApproved = false

  if (filterProps.value) {
    filterProps.value.forEach((value) => {
      switch (value) {
        case 'image-small':
          pixelRange[pixelRangeCounter] = ['1', '999999']
          pixelRangeCounter += 1
          formattedFilters.pixel_range = pixelRange
          break
        case 'image-medium':
          pixelRange[pixelRangeCounter] = ['1000000', '2000000']
          pixelRangeCounter += 1
          formattedFilters.pixel_range = pixelRange
          break
        case 'image-large':
          pixelRange[pixelRangeCounter] = ['2000001', '']
          pixelRangeCounter += 1
          formattedFilters.pixel_range = pixelRange
          break
        case 'landscape':
        case 'portrait':
        case 'square':
          aspectRatio.push(value)
          formattedFilters.aspect_ratio = aspectRatio
          break
        case 'audio':
        case 'file':
        case 'image':
        case 'video':
          assetType.push(value)
          formattedFilters.asset_type = assetType
          break
        case 'asset_license_creative_commons':
        case 'asset_license_royalty_free':
        case 'asset_license_rights_managed':
          formattedFilters.asset_license = value
          break
        case 'asset_approved':
          availability.context_type = approvedSection.context_type
          availability.context_id = String(approvedSection.context_id)
          formattedFilters.availability = availability
          break
        case 'asset_unapproved':
          removeAssetApproved = true
          break
        default:
          break
      }
    })
  }

  // asset_unapproved removes `asset_usage_status: approved`
  // but for whatever reason asset_usage_status is present when no other
  // usage status filters are included
  if (removeAssetApproved) {
    delete formattedFilters.asset_usage_status
  }

  return formattedFilters
}

export const searchTerms = (sidebarSettings) => sidebarSettings.searchTerms

export default libraryFilterTree
